import React, { createContext, useContext, useState, useRef, useEffect, ReactNode } from 'react';

// Define types
interface Track {
  id: string;
  title: string; 
  artist: string;
  url: string;
  coverArt?: string;
  album?: string;
}

interface AudioContextType {
  currentTrack: Track | null;
  isPlaying: boolean;
  progress: number;
  duration: number;
  volume: number;
  isPreviewMode: boolean;
  previewTimeRemaining: number;
  play: (track: Track) => void;
  pause: () => void;
  playTrack: (track: Track) => void;
  togglePlayPause: () => void;
  setVolume: (value: number) => void;
  seek: (time: number) => void;
  nextTrack: () => void;
  prevTrack: () => void;
  addToQueue: (track: Track) => void;
  removeFromQueue: (id: string) => void;
}

const defaultContext: AudioContextType = {
  currentTrack: null,
  isPlaying: false,
  progress: 0,
  duration: 0,
  volume: 0.8,
  isPreviewMode: true,
  previewTimeRemaining: 30,
  play: () => {},
  pause: () => {},
  playTrack: () => {},
  togglePlayPause: () => {},
  setVolume: () => {},
  seek: () => {},
  nextTrack: () => {},
  prevTrack: () => {},
  addToQueue: () => {},
  removeFromQueue: () => {},
};

const AudioContext = createContext<AudioContextType>(defaultContext);

// Provider component
export const AudioProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.8);
  const [queue, setQueue] = useState<Track[]>([]);
  const [history, setHistory] = useState<Track[]>([]);
  
  // Preview mode state
  const [isPreviewMode] = useState(true); // Always in preview mode for this licensing site
  const [previewTimeRemaining, setPreviewTimeRemaining] = useState(30);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fadeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Preview settings
  const PREVIEW_DURATION = 30; // 30 seconds
  const FADE_START_TIME = 25; // Start fade at 25 seconds (last 5 seconds)
  const FADE_DURATION = 5; // 5 seconds fade duration
  
  useEffect(() => {
    // Create audio element if it doesn't exist
    if (!audioRef.current) {
      audioRef.current = new Audio();
      
      // Add event listeners
      audioRef.current.addEventListener('timeupdate', updateProgress);
      audioRef.current.addEventListener('loadedmetadata', setAudioDuration);
      audioRef.current.addEventListener('ended', handleTrackEnd);
      
      // Set initial volume
      audioRef.current.volume = volume;
    }
    
    // Cleanup function
    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', updateProgress);
        audioRef.current.removeEventListener('loadedmetadata', setAudioDuration);
        audioRef.current.removeEventListener('ended', handleTrackEnd);
        audioRef.current.pause();
      }
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
      }
    };
  }, []);
  
  // Set audio source when currentTrack changes
  useEffect(() => {
    if (currentTrack && audioRef.current) {
      audioRef.current.src = currentTrack.url;
      audioRef.current.load();
      
      // Reset preview state
      setPreviewTimeRemaining(PREVIEW_DURATION);
      
      // In preview mode, immediately set duration to preview length
      if (isPreviewMode) {
        setDuration(PREVIEW_DURATION);
      }
      
      // Reset volume to default and clear any fade
      if (audioRef.current) {
        audioRef.current.volume = volume;
      }
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }
      
      // Set up audio event listeners
      const audio = audioRef.current;
      audio.addEventListener('loadedmetadata', setAudioDuration);
      audio.addEventListener('ended', handleTrackEnd);
      
      if (isPlaying) {
        audioRef.current.play()
          .catch(error => {
            console.error('Error playing audio:', error);
            setIsPlaying(false);
          });
      }
      
      // Cleanup event listeners
      return () => {
        audio.removeEventListener('loadedmetadata', setAudioDuration);
        audio.removeEventListener('ended', handleTrackEnd);
      };
    }
  }, [currentTrack]);
  
  // Handle play/pause changes
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play()
          .catch(error => {
            console.error('Error playing audio:', error);
            setIsPlaying(false);
          });
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);
  
  // Set up progress update interval
  useEffect(() => {
    let progressInterval: NodeJS.Timeout | null = null;
    
    if (isPlaying && audioRef.current) {
      // Update progress every 100ms for smooth progress bar
      progressInterval = setInterval(updateProgress, 100);
    }
    
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [isPlaying]);
  
  // Update progress and handle preview logic
  const updateProgress = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.currentTime;
      setProgress(currentTime);
      
      if (isPreviewMode && currentTime > 0) {
        const remaining = Math.max(0, PREVIEW_DURATION - currentTime);
        setPreviewTimeRemaining(remaining);
        
        // Start fade-out during last 5 seconds
        if (currentTime >= FADE_START_TIME && !fadeIntervalRef.current) {
          startFadeOut();
        }
        
        // Stop playback at preview duration
        if (currentTime >= PREVIEW_DURATION) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          setIsPlaying(false);
          setProgress(0);
          setPreviewTimeRemaining(PREVIEW_DURATION);
          if (fadeIntervalRef.current) {
            clearInterval(fadeIntervalRef.current);
            fadeIntervalRef.current = null;
          }
        }
      }
    }
  };
  
  // Start fade-out effect for the last 5 seconds
  const startFadeOut = () => {
    if (!audioRef.current || fadeIntervalRef.current) return;
    
    const startVolume = audioRef.current.volume;
    const fadeSteps = 50; // Number of fade steps
    const fadeInterval = (FADE_DURATION * 1000) / fadeSteps; // Time between steps
    const volumeDecrement = startVolume / fadeSteps;
    
    let currentStep = 0;
    
    fadeIntervalRef.current = setInterval(() => {
      if (!audioRef.current) {
        if (fadeIntervalRef.current) {
          clearInterval(fadeIntervalRef.current);
          fadeIntervalRef.current = null;
        }
        return;
      }
      
      currentStep++;
      const newVolume = Math.max(0, startVolume - (volumeDecrement * currentStep));
      audioRef.current.volume = newVolume;
      
      // Clear interval when fade is complete
      if (currentStep >= fadeSteps || newVolume <= 0) {
        clearInterval(fadeIntervalRef.current!);
        fadeIntervalRef.current = null;
      }
    }, fadeInterval);
  };
  
  // Set audio duration
  const setAudioDuration = () => {
    if (audioRef.current) {
      // In preview mode, always show preview duration
      if (isPreviewMode) {
        setDuration(PREVIEW_DURATION);
      } else {
        setDuration(audioRef.current.duration);
      }
    }
  };
  
  // Handle track end
  const handleTrackEnd = () => {
    if (isPreviewMode) {
      // In preview mode, don't auto-advance to next track
      setIsPlaying(false);
      setProgress(0);
      setPreviewTimeRemaining(PREVIEW_DURATION);
    } else {
      nextTrack();
    }
  };
  
  // Play a track
  const play = (track: Track) => {
    // Add current track to history if it exists
    if (currentTrack) {
      setHistory(prev => [currentTrack, ...prev.slice(0, 19)]);
    }
    
    setCurrentTrack(track);
    setIsPlaying(true);
  };
  
  // Pause playback
  const pause = () => {
    setIsPlaying(false);
    // Clear any active fade when pausing
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }
  };
  
  // Toggle play/pause
  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };
  
  // Set volume
  const setVolume = (value: number) => {
    if (audioRef.current) {
      audioRef.current.volume = value;
      setVolumeState(value);
      // Clear any active fade when manually setting volume
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }
    }
  };
  
  // Seek to position (limited in preview mode)
  const seek = (time: number) => {
    if (audioRef.current) {
      // In preview mode, limit seeking to preview duration
      const seekTime = isPreviewMode ? Math.min(time, PREVIEW_DURATION) : time;
      audioRef.current.currentTime = seekTime;
      setProgress(seekTime);
    }
  };
  
  // Play next track
  const nextTrack = () => {
    if (queue.length > 0) {
      // Get next track from queue
      const nextTrack = queue[0];
      const newQueue = queue.slice(1);
      
      // Add current track to history if it exists
      if (currentTrack) {
        setHistory(prev => [currentTrack, ...prev.slice(0, 19)]);
      }
      
      // Play next track
      setCurrentTrack(nextTrack);
      setQueue(newQueue);
      setIsPlaying(true);
    } else {
      // No more tracks in queue
      setIsPlaying(false);
    }
  };
  
  // Play previous track
  const prevTrack = () => {
    if (history.length > 0) {
      // Get previous track from history
      const prevTrack = history[0];
      const newHistory = history.slice(1);
      
      // Add current track to queue if it exists
      if (currentTrack) {
        setQueue(prev => [currentTrack, ...prev]);
      }
      
      // Play previous track
      setCurrentTrack(prevTrack);
      setHistory(newHistory);
      setIsPlaying(true);
    }
  };
  
  // Add track to queue
  const addToQueue = (track: Track) => {
    setQueue(prev => [...prev, track]);
  };
  
  // Remove track from queue
  const removeFromQueue = (id: string) => {
    setQueue(prev => prev.filter(track => track.id !== id));
  };
  
  
  // Alias for play to match naming used in components
  const playTrack = play;
  
  const value = {
    currentTrack,
    isPlaying,
    progress,
    duration,
    volume,
    isPreviewMode,
    previewTimeRemaining,
    play,
    pause,
    playTrack,
    togglePlayPause,
    setVolume,
    seek,
    nextTrack,
    prevTrack,
    addToQueue,
    removeFromQueue,
  };
  
  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};

// Custom hook to use the audio context
export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

export default AudioContext;